import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table, Tag, Space, Typography, Tooltip, Empty } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SyncOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Card from "../../card/Card";
import { Button } from "../../ui";
import { useTheme } from "../../../contexts/ThemeContext";
import ExcelAiService from "../../../services/excelAiService";

const { Text } = Typography;

const STATUS_EM_ANDAMENTO = ["Pendente", "Executando", "Sincronizando"];

/** Intervalos de polling, em ms. Sobe progressivamente para não martelar o servidor. */
const POLL_INICIAL = 3000;
const POLL_MAXIMO = 15000;

const formatarData = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const formatarDuracao = (inicio, fim) => {
  if (!inicio) return "-";
  const ini = new Date(inicio).getTime();
  const f = fim ? new Date(fim).getTime() : Date.now();
  const seg = Math.max(0, Math.round((f - ini) / 1000));
  if (seg < 60) return `${seg}s`;
  return `${Math.floor(seg / 60)}m${String(seg % 60).padStart(2, "0")}s`;
};

/**
 * Traduz o estado do integrador para o que o usuário precisa entender.
 *
 * "Falhou" é problema técnico, que o usuário não resolve; "com ressalvas" é dado que ficou de fora
 * e que ele PODE resolver. Chamar os dois de "erro" faria ele procurar defeito onde não há — e
 * ignorar o que realmente depende dele.
 *
 * "Bloqueado" só aparece em runs antigos: pendência não interrompe mais o processamento.
 */
const descreverStatus = (run, theme) => {
  const status = run?.status;
  const criticas = run?.pendenciasCriticas || 0;
  const atencao = run?.pendenciasAtencao || 0;

  if (STATUS_EM_ANDAMENTO.includes(status)) {
    return { texto: "Processando", cor: theme.primary, icone: <SyncOutlined spin /> };
  }
  if (status === "Erro") {
    return { texto: "Falhou", cor: theme.error, icone: <ExclamationCircleOutlined /> };
  }
  if (status === "Bloqueado") {
    return { texto: "Não carregado", cor: theme.error, icone: <StopOutlined /> };
  }
  if (criticas > 0) {
    return { texto: "Concluído com ressalvas", cor: theme.warning, icone: <ExclamationCircleOutlined /> };
  }
  if (status === "SucessoComAlertas" || atencao > 0) {
    return { texto: "Concluído com alertas", cor: theme.warning, icone: <ExclamationCircleOutlined /> };
  }
  if (status === "Sucesso") {
    return { texto: "Concluído", cor: theme.success, icone: <CheckCircleOutlined /> };
  }
  return { texto: status || "-", cor: theme.textSecondary, icone: null };
};

/**
 * Últimos processamentos, com status e alertas.
 *
 * Acompanhamento por POLLING com backoff, deliberadamente — não por SSE. O produto já teve
 * problema de CPU com streams abertos de madrugada sem ninguém usando; aqui o polling para em
 * estado terminal, ao desmontar o componente e quando a aba perde o foco.
 */
const ExcelAiRunsCard = ({ syncId, atualizarSinal, onAbrirDetalhe }) => {
  const { theme } = useTheme();
  const [runs, setRuns] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [indisponivel, setIndisponivel] = useState(false);

  const timerRef = useRef(null);
  const intervaloRef = useRef(POLL_INICIAL);
  const montadoRef = useRef(true);

  const temRunAtivo = useMemo(
    () => runs.some((r) => STATUS_EM_ANDAMENTO.includes(r.status)),
    [runs],
  );

  const buscar = useCallback(
    async (mostrarLoading = false) => {
      if (!syncId) return [];
      try {
        if (mostrarLoading) setCarregando(true);
        const lista = await ExcelAiService.listarRuns(syncId, 10);
        if (!montadoRef.current) return [];
        setRuns(lista);
        setIndisponivel(false);
        return lista;
      } catch (erro) {
        console.error("Erro ao listar processamentos:", erro);
        if (montadoRef.current) setIndisponivel(true);
        return [];
      } finally {
        if (montadoRef.current && mostrarLoading) setCarregando(false);
      }
    },
    [syncId],
  );

  useEffect(() => {
    montadoRef.current = true;
    buscar(true);
    return () => {
      montadoRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [buscar]);

  // Reconsulta imediata quando a tela dispara um processamento.
  useEffect(() => {
    if (atualizarSinal === undefined) return;
    intervaloRef.current = POLL_INICIAL;
    buscar(false);
  }, [atualizarSinal, buscar]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    // Nada em andamento: não há o que observar. É esta linha que impede o polling eterno.
    if (!temRunAtivo || indisponivel) {
      intervaloRef.current = POLL_INICIAL;
      return undefined;
    }

    const agendar = () => {
      timerRef.current = setTimeout(async () => {
        // Aba em segundo plano não precisa de atualização: reagenda sem consultar.
        if (document.hidden) {
          agendar();
          return;
        }

        await buscar(false);
        if (!montadoRef.current) return;

        intervaloRef.current = Math.min(intervaloRef.current * 1.5, POLL_MAXIMO);
        agendar();
      }, intervaloRef.current);
    };

    agendar();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [temRunAtivo, indisponivel, buscar]);

  const colunas = [
    {
      title: "Quando",
      key: "quando",
      width: 140,
      render: (_, run) => formatarData(run.iniciadoEmUtc || run.criadoEmUtc),
    },
    {
      title: "Status",
      key: "status",
      width: 210,
      render: (_, run) => {
        const { texto, cor, icone } = descreverStatus(run, theme);
        return (
          <Space size={6}>
            <span style={{ color: cor }}>{icone}</span>
            <Text style={{ color: cor, fontSize: 13, fontWeight: 500 }}>{texto}</Text>
          </Space>
        );
      },
    },
    {
      title: "Duração",
      key: "duracao",
      width: 90,
      render: (_, run) => formatarDuracao(run.iniciadoEmUtc, run.concluidoEmUtc),
    },
    {
      title: "Registros",
      dataIndex: "totalRegistros",
      key: "totalRegistros",
      width: 100,
      render: (v) => (v ? v.toLocaleString("pt-BR") : "—"),
    },
    {
      title: "Alertas",
      key: "alertas",
      render: (_, run) => {
        const b = run.pendenciasCriticas || 0;
        const a = run.pendenciasAtencao || 0;
        const i = run.pendenciasInformativas || 0;
        if (!b && !a && !i) return <Text style={{ color: theme.textSecondary }}>—</Text>;
        return (
          <Space size={4} wrap>
            {b > 0 && <Tag color="error">{b} não carregado{b > 1 ? "s" : ""}</Tag>}
            {a > 0 && <Tag color="warning">{a} atenção</Tag>}
            {i > 0 && <Tag>{i} info</Tag>}
          </Space>
        );
      },
    },
    {
      title: "",
      key: "acoes",
      width: 120,
      render: (_, run) => {
        const temPendencia = (run.pendenciasCriticas || 0) + (run.pendenciasAtencao || 0) > 0;
        return (
          <Button type="link" size="small" onClick={() => onAbrirDetalhe?.(run)}>
            {temPendencia ? "Resolver" : "Ver detalhes"}
          </Button>
        );
      },
    },
  ];

  return (
    <Card
      title={<span style={{ fontSize: 16, fontWeight: 600 }}>Processamentos</span>}
      extra={
        <Tooltip title="Atualizar">
          <Button
            type="text"
            icon={<ReloadOutlined />}
            loading={carregando}
            onClick={() => buscar(true)}
          />
        </Tooltip>
      }
      style={{ marginBottom: 24 }}
    >
      {indisponivel ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Não foi possível consultar os processamentos agora. Tente atualizar em alguns minutos."
        />
      ) : (
        <Table
          size="small"
          rowKey={(run) => run.runId}
          loading={carregando}
          columns={colunas}
          dataSource={runs}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhum processamento ainda"
              />
            ),
          }}
        />
      )}
    </Card>
  );
};

export default ExcelAiRunsCard;
